import React, { useContext, useMemo } from 'react';
import { useIntl } from 'umi';
import { TableColumn } from 'tea-component';
import {
  formatExeclCommon,
  formatExeclNormalPercent,
  formatSmallValue,
  getChartDateFormatString
  , commonValueFormatter } from '@/pages/dashboard/utils/utils';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';

import Revenue from '../../../components/Revenue';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { formatConfigType } from '@/pages/dashboard/components/Chart/_config/interface';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { isArray, isEmpty } from 'lodash-es';

function PcRevenue(props: any) {
  const contextObj = useContext(FilterContext);
  const { filterVal: formValue } = contextObj || {};
  const { data: defaultData = [], enName, cnName, defaultActive = 1, config } = props;
  const { data: dataSource, loading, errorMsg } = defaultData?.PcMonetizationRevenueData || {};
  const {
    chart: { type, decimal },
    componentMetricsConfig
  } = config;
  const data = dataSource?.mtx_revenue;
  const { timeType = 'Day' } = formValue || {};
  const { tableOption, chart, tableData, downloadData } = usePCRevenueColumns(data, componentMetricsConfig, timeType);
  const revenueData = useMemo(() => data ?? [], [data]);

  const options = useMemo(() => ({
    chart,
    table: {
      option: tableOption,
      data: tableData,
      downloadData
    }
  }), [chart, downloadData, tableData, tableOption]);

  return (
    <Revenue
      {...{
        otherOption: options,
        data: {
          MonetizationRevenueData: {
            data: {
              revenue: revenueData
            },
            loading,
            errorMsg
          }
        },
        enName,
        cnName,
        barAndLineFormatterFunc: (params: any) => barFormatterPcRevenueFunc?.(params, { type, decimal }),
        defaultActive,
        isShowLegend: true,
        type,
        decimal
      }}
    ></Revenue>
  );
}

export default PcRevenue;

export const barFormatterPcRevenueFunc = (
  param: any[],
  config?: formatConfigType & { type: 'int' | 'decimals' | 'percent'; decimal: number; }
) => {
  let title = '';
  // 浮窗数据是否排序
  const {
    isSort,
    isShowWeekDay,
    isRemoveNull,
    // isPercent = false,
    // isDollar = false,
    // isUserQuota = false,
    metricsName = '',
    type,
    decimal
  } = config || {};
  const data = param?.map(item => {
    if (!title) title = item.axisValue;
    const isMinimum = ['ARPU'];
    return {
      name: item.seriesName,
      metricsNum: isMinimum.includes(item.seriesName)
        ? formatSmallValue(item?.value, type, decimal)
        : commonValueFormatter(type, item?.value, decimal),
      color: item.color
    };
  });
  return getEchartTooltipText({
    metricsName,
    title,
    isShowWeekDay,
    data,
    isSort,
    isRemoveNull
  });
};

export const usePCRevenueColumns = <A extends any[], S = string, B = boolean>(
// 接口返回的数据
  revenueData: A[],
// 配置源数据，整个图表以及表格根据这个来进行渲染
  componentMetricsConfig: A,
// 时间粒度
  timeType: S,
// 是否需要开启有多个指标的情况下,只渲染一个指标，TODO:有时间处理成指定的指标，目前默认是第一条
  isOnlyShow?: B,
// 是否需要开启有时间粒度的情况下，做映射关系，例如表头等
  isTimeGranularity?: B
) => {
  const { getWeekDay } = useWeekDay();
  const { formatMessage, locale } = useIntl();
  const TitleName: { [key in string]: string } = useMemo(() => ({
    Day: formatMessage({ id: 'monetization_revenue_DAU' }),
    Week: formatMessage({ id: 'monetization_revenue_WAU' }),
    Month: formatMessage({ id: 'monetization_revenue_MAU' })
  }), [formatMessage]);
  const isEn = locale === 'en-US';
  // 表头固定列
  const tableOption: Partial<TableColumn[] | unknown[]> = useMemo(() => {
    if (!isArray(componentMetricsConfig) || isEmpty(componentMetricsConfig)) return [];
    return [
      {
        key: 'date',
        align: 'left',
        fixed: 'left',
        width: 160,
        header: formatMessage({ id: 'monetization_revenue_Date' }),
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return (
            <>
              <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
              {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
            </>
          );
        }
      },
      ...((!isEmpty(componentMetricsConfig) &&
				componentMetricsConfig?.map((task: { metricKey: string; enName: string; cnName: string; }) => ({
				  key: task?.metricKey,
				  align: 'right',
				  header:
						isTimeGranularity && task.metricKey === 'dau' ? TitleName?.[timeType as string] : isEn ? task?.enName : task?.cnName
				}))) ||
				[])
    ];
  }, [componentMetricsConfig, formatMessage, getWeekDay, timeType, isTimeGranularity, TitleName, isEn]);
  const { chart, tableData, downloadData } = useMemo(() => {
    if (!isArray(componentMetricsConfig) || isEmpty(componentMetricsConfig)) return {};
    // 图表数组
    const chart: any[] = [];
    const metricsList = componentMetricsConfig;
    let chartsList = componentMetricsConfig;
    if (isOnlyShow && !isEmpty(chartsList)) {
      chartsList = chartsList.slice(0, 1) as any;
    }
    chartsList?.map((metric: any) => {
      const columnObj = {
        name: isEn ? metric?.enName : metric?.cnName,
        barCategoryGap: '50%',
        data: [] as any[]
      };
      if (metric?.isDeleteChartTipsDollar) {
        columnObj.name = columnObj?.name?.split('($)')?.[0] ?? columnObj?.name;
      }
      revenueData?.map((item: any) => {
        columnObj.data.push({
          x: getChartDateFormatString(timeType, item?.date, true),
          y: item?.metrics?.[metric?.metricKey]?.value
        });
      });
      columnObj.data.length && chart.push(columnObj);
    });
    // 表数据
    const tableData: any = [];
    const downloadData: any = [];
    revenueData?.map((item: any) => {
      const date = getChartDateFormatString(timeType, item?.date, true);
      const obj: any = { date };
      const downObj: any = { date };
      metricsList?.map((metric: any) => {
        const isMinimum = ['arpu'];
        const value = item?.metrics?.[metric?.metricKey]?.value;
        obj[metric?.metricKey] = isMinimum.includes(metric?.metricKey)
          ? formatSmallValue(value, metric?.type, metric?.decimal)
          : commonValueFormatter(metric?.type, value, metric?.decimal);
        downObj[metric?.metricKey] = metric?.isPercent
          ? formatExeclNormalPercent(value ?? '-')
          : formatExeclCommon(
            isMinimum.includes(metric?.metricKey) ? formatSmallValue(value, metric?.type, metric?.decimal) : commonValueFormatter(metric?.type, value, metric?.decimal)
					  );
      });
      tableData.push(obj);
      downloadData.push(downObj);
    });
    return {
      chart,
      tableData,
      downloadData
    };
  }, [componentMetricsConfig, isOnlyShow, revenueData, isEn, timeType]);
  return {
    tableOption,
    chart,
    tableData,
    downloadData
  };
};
